// Schreiben-Assistent (#586): ein Ablauf für E-Mail und Post – Vorlage → Empfänger → Inhalt → Versand.
// Vorbelegung über die URL: ?empfaenger=<art>:<id> (mehrfach), ?objekt=<id>, ?anlage=drive:<fileId>:<name>.
// Spec: docs/superpowers/specs/2026-10-31-schreiben-assistent-design.md
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CaretLeft, Files, PaperPlaneTilt, PencilSimple, Users } from '@phosphor-icons/react';
import { ImmoButton, Schrittleiste } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { ladeSchreibenEmpfaenger } from '../../../lib/schreiben/empfaenger';
import type { EmpfaengerArt } from '../../../lib/schreiben/typen';
import { mimeAusName } from '../../../lib/epost/emailZuBrief';
import { SchrittVorlage } from './SchrittVorlage';
import { SchrittEmpfaenger } from './SchrittEmpfaenger';
import { SchrittInhalt } from './SchrittInhalt';
import { SchrittVersand } from './SchrittVersand';
import { START, type SetZustand, type Zustand } from './zustand';
import { neuLadenGestaltung, useGestaltung } from './useGestaltung';
import { standardAbsender } from '../../../lib/schreiben/gestaltung';
import { supabase } from '../../../lib/supabase/client';

type Schritt = 0 | 1 | 2 | 3;
const SCHRITTE = [
  { label: 'Vorlage', icon: Files }, { label: 'Empfänger', icon: Users }, { label: 'Inhalt', icon: PencilSimple }, { label: 'Versand', icon: PaperPlaneTilt },
] as const;
const ARTEN: EmpfaengerArt[] = ['mieter', 'eigentuemer', 'kontakt', 'dienstleister'];

export default function SchreibenAssistent() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { mobil } = useMobilModus();
  const [schritt, setSchritt] = useState<Schritt>(0);
  const [zustand, setZustandRoh] = useState<Zustand>(START);
  const setZustand: SetZustand = (f) => setZustandRoh((z) => f(z));

  // Etappe 2: Absender = der mit dem eigenen Profil verknüpfte (einmal, sobald die Liste da ist)
  const gestaltung = useGestaltung();
  const absenderGesetzt = React.useRef(false);
  useEffect(() => () => neuLadenGestaltung(), []);
  useEffect(() => {
    if (!gestaltung.bereit || absenderGesetzt.current) return;
    absenderGesetzt.current = true;
    void supabase.auth.getUser().then(({ data }) => {
      const id = standardAbsender(gestaltung.absender, data.user?.id ?? null);
      if (id) setZustand((z) => (z.absenderId ? z : { ...z, absenderId: id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gestaltung.bereit]);

  // Vorbelegung aus der URL (einmal)
  useEffect(() => {
    const anlagen = params.getAll('anlage').map((a) => {
      const [quelle, id, ...name] = a.split(':');
      const n = name.join(':') || 'Anlage.pdf'; // useSearchParams hat schon dekodiert
      return quelle === 'drive' && id ? { name: n, mime: mimeAusName(n), drive: id } : null;
    }).filter((x): x is NonNullable<typeof x> => !!x);
    if (anlagen.length) setZustand((z) => ({ ...z, anlagen }));
    const ziele = params.getAll('empfaenger').map((x) => x.split(':')).filter(([art, id]) => ARTEN.includes(art as EmpfaengerArt) && id);
    if (ziele.length) {
      void Promise.all(ziele.map(([art, id]) => ladeSchreibenEmpfaenger({ art: art as EmpfaengerArt, id, name: '', sub: '' }).catch(() => null)))
        .then((liste) => setZustand((z) => ({ ...z, empfaenger: liste.filter((e): e is NonNullable<typeof e> => !!e) })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weiterErlaubt = schritt === 0 ? true : schritt === 1 ? zustand.empfaenger.length > 0 : true;
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
        <ImmoButton groesse={mobil ? 44 : 40} icon={CaretLeft} onClick={() => navigate(-1)}>Zurück</ImmoButton>
        <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
          <h1 className="m-0" style={{ fontSize: mobil ? 22 : 26, fontWeight: 800 }}>Neues Schreiben</h1>
          <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Per E-Mail, per Post oder beides – aus einer Vorlage, mit Ablage in der Akte</span>
        </div>
      </div>
      <Schrittleiste schritte={SCHRITTE} aktuell={schritt} mobil={mobil}
        darfWaehlen={(i) => i < schritt || (i === schritt + 1 && weiterErlaubt) || (i <= 3 && zustand.empfaenger.length > 0 && i > 0)}
        onWahl={(i) => setSchritt(i as Schritt)} />

      {schritt === 0 && <SchrittVorlage zustand={zustand} setZustand={setZustand} weiter={() => setSchritt(1)} />}
      {schritt === 1 && <SchrittEmpfaenger zustand={zustand} setZustand={setZustand} />}
      {schritt === 2 && <SchrittInhalt zustand={zustand} setZustand={setZustand} />}
      {schritt === 3 && <SchrittVersand zustand={zustand} setZustand={setZustand} />}

      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        {schritt > 0 ? <ImmoButton groesse={44} icon={CaretLeft} onClick={() => setSchritt((s) => (s - 1) as Schritt)}>Zurück</ImmoButton> : <span />}
        {schritt < 3 && (
          <ImmoButton art="akzent" groesse={44} onClick={() => setSchritt((s) => (s + 1) as Schritt)} disabled={!weiterErlaubt}>
            {schritt === 0 ? (zustand.vorlageId ? 'Weiter' : 'Weiter mit freiem Text') : 'Weiter'}
          </ImmoButton>
        )}
      </div>
    </div>
  );
}
